import React, { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, Link } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import { getOneRosterUsers, getOneRosterRoles } from '../../store/OneRoster/OneRosterUserAction';
import StatusCode from '../../util/StatusCode';
import debounce from 'lodash/debounce';
import * as Helper from '../../util/Helper';
import ActiveToggle from '../misc/ActiveToggle';
import TableHorizontal from '../misc/TableHorizontal';
import SelectInput from '../misc/SelectInput';
import Search from '../misc/Search';
import FilterBottomComponent from '../misc/FilterBottomComponent';
import RequestParser from '../../util/RequestParser';
import { PageResult } from '../../models/CoreModels';
import { CoreOneRosterUser } from '../../models/CoreOneRosterModels';
import { CacheRequestState } from '../../models/RequestState';
import { RoleType } from '../../util/OneRoster/OneRosterRoleTypeUtil';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';


const OneRosterUsers = () => {
    const { t } = useTranslation(['common', 'oneRoster']);

    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const users = useSelector<RootState, CacheRequestState<PageResult<CoreOneRosterUser>>>(state => state.onerosterUser.users);
    const roles = useSelector<RootState, RoleType[]>(state => state.onerosterUser.roles);
    var filter = RequestParser.parseOneRosterUserSearchPageRequest(searchParams);

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
        getOneRosterUsers(filter)(dispatch);
        getOneRosterRoles()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        getOneRosterUsers(filter, true)(dispatch);
        window.scrollTo(0, 0);
    }, [searchParams]);

    const updateFilter = (name: string, value: string | number | boolean | undefined) => {
        if (name !== 'page') {
            searchParams.set('page', '1');
        }

        if (value !== undefined && value !== '' && value !== null) {
            if (typeof (value) === 'string') {
                searchParams.set(name, value);
            } else {
                searchParams.set(name, value.toString());
            }
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }

    const debouncedSearch = useCallback(
        debounce(search => {
            updateFilter('search', search);
        }, 300), [searchParams]);

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        let search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const mapUsers = () => {
        if (users.code === StatusCode.COMPLETE || (users.code === StatusCode.PENDING && users.data !== undefined)) {
            if (users.data === undefined) {
                return [];
            }
            return users.data?.values.map(u => ({
                id: u.id,
                data: [
                    { value: <Link to={`/oneroster/user/${u.id}/information`}> {`${u.givenName} ${u.familyName}`}</Link> },
                    { value: u.userName },
                    { value: u.schoolCount },
                    { value: u.classCount },
                    { value: t(`roleTypes.${u.role}`, { ns: 'oneRoster' }) },
                    { value: Helper.active(u.active) },
                    { value: Helper.formatDate(u.created), className: 'nowrap' },
                    { value: Helper.formatDate(u.modified), className: 'nowrap' },
                ]
            }));
        } else {
            return [];
        }
    };

    const renderClasses = () => {
        return (
            <TableHorizontal
                header={[{ value: t('genericTableHeaders.name', { ns: 'common' }) }, { value: t('commonTableHeaders.userName', { ns: 'oneRoster' }) }, { value: t('userTableHeaders.schools', { ns: 'oneRoster' }) }, { value: t('commonTableHeaders.sections', { ns: 'oneRoster' }) }, { value: t('commonTableHeaders.role', { ns: 'oneRoster' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }, { value: t('genericTableHeaders.created', { ns: 'common' }) }, { value: t('genericTableHeaders.modified', { ns: 'common' }) }]}
                info={mapUsers()}
                error={users.code === StatusCode.ERROR}
                searching={users.code === StatusCode.PENDING}
                displayName={t('object.oneRoster.user', { ns: 'common', count: 2 })}
                noResMsg={t('notFound.multiple', { ns: 'oneRoster', type: t('type.users', { ns: 'oneRoster' }) })}
            />
        );
    };

    const renderFilter = () => (
        <Row>
            <Col sm="6">
                <Search value={search} onChange={onSearch} placeholder={t('search', { ns: 'oneRoster', type: t('type.users', { ns: 'oneRoster' }) })} />
            </Col>

            <Col lg="4">
                <SelectInput
                    items={roles.map(role => ({ value: role.toString(), text: `${t('filter.on', { ns: 'common' })} ${t(`roleTypes.${role.toString()}`, {ns: 'oneRoster'})} ` }))}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='role'
                    selected={filter.role}
                    defaultText={t('filter.role', { ns: 'common' })}
                />
            </Col>

            <Col sm="2">
                <ActiveToggle
                    onlyActive={filter.onlyActive ?? true}
                    update={(active) => updateFilter('onlyActive', active)}
                    disabled={users.code === StatusCode.PENDING}
                />
            </Col>
        </Row>
    )

    return (
        <div className="container users_oneroster">
            <div className="header--fixed">
                <h1>{t('object.oneRoster.user', { ns: 'common', count: 2 })}</h1>
                {renderFilter()}
            </div>
            <div className="body--scroll padding-40">
                {renderClasses()}
            </div>

            <FilterBottomComponent
                clearFilter={clearFilter}
                updateFilter={(value) => updateFilter('page', value)}
                requestState={users}
            />

        </div>
    )
}

export default OneRosterUsers;
